import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { take } from 'rxjs';
import { ApiCulture, LanguageCode } from '@core/models';
import {
  apiCultureFor,
  LANGUAGE_STORAGE_KEY,
  normalizeLanguage,
  resolveInitialLanguage,
} from './language-preferences';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);
  private readonly currentLanguageSignal = signal<LanguageCode>('en');
  private readonly translationRevision = signal(0);
  private requestedLanguage: LanguageCode = 'en';

  readonly currentLanguage = this.currentLanguageSignal.asReadonly();
  readonly apiCulture = computed<ApiCulture>(() => apiCultureFor(this.currentLanguage()));
  readonly locale = this.apiCulture;

  constructor() {
    this.setLanguage(resolveInitialLanguage(), false);
  }

  setLanguage(language: LanguageCode, persist = true): void {
    const normalized = normalizeLanguage(language);
    this.requestedLanguage = normalized;
    this.transloco.setActiveLang(normalized);
    document.documentElement.lang = normalized;

    if (persist) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    }

    this.transloco
      .load(normalized)
      .pipe(take(1))
      .subscribe({
        next: () => {
          if (this.requestedLanguage === normalized) {
            this.currentLanguageSignal.set(normalized);
            this.translationRevision.update((revision) => revision + 1);
          }
        },
        error: () => {
          if (this.requestedLanguage === normalized) {
            this.currentLanguageSignal.set(normalized);
            this.translationRevision.update((revision) => revision + 1);
          }
        },
      });
  }

  t(key: string, params?: Record<string, unknown>): string {
    this.translationRevision();
    return this.transloco.translate(key, params, this.currentLanguage());
  }

  normalizeLanguage(language: string | null | undefined): LanguageCode {
    return normalizeLanguage(language);
  }
}
